import {ChangeDetectionStrategy, Component, computed, DestroyRef, Inject, Injectable, signal} from "@angular/core";
import {AkseptertSignaturmetode, GruppeId, GruppeMedlemListeModell, HtmlTekst, MøteId, MøteModell, ProtokollførtMøtedeltakerModell, ProtokollRessursClient, SendProtokollsigneringsInvitasjonSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {firstValueFrom, Observable} from "rxjs";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {GruppeDatatjeneste} from "felles-ng2/tjenester/datalager/GruppeDatatjeneste";
import {map} from "rxjs/operators";
import {signatarer} from "felles-ng2/modell/protokollModellHjelpere";
import {MøteDatatjeneste} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {SigneringVelgAksepterteMetoderKomponent} from "../../../signering/komponenter/signering-velg-aksepterte-metoder/signering-velg-aksepterte-metoder";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MatToolbar} from "@angular/material/toolbar";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {TranslateModule} from "@ngx-translate/core";
import {erLikeListerIgnorerRekkefølge, erTomListe} from "felles-ng2/støttefunksjoner/lister";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {MøtePubliserDialogKomponent} from "../../../møte/modale/møte-publiser-dialog/møte-publiser-dialog";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatTooltip} from "@angular/material/tooltip";
import {MedlemmerListevisningKompaktKomponent} from "web-ng2/moduler/medlem/komponenter/medlemmer-listevisning-kompakt/medlemmer-listevisning-kompakt";
import {MeldingSendtIkonKomponent} from "web-ng2/delt/komponenter/melding-sendt-ikon/melding-sendt-ikon";
import {tomMøteModell} from "felles-ng2/modell/tommeModeller";
import {SubmitButtonKomponent} from "web-ng2/delt/komponenter/skjema/submit-button/submit-button";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";

const debug = initDebugger('MøteprotokollSendTilSigneringKomponent');

/**
 * Dialog for å sende protokoll til signering
 */
@Component({
    selector: 'moeteprotokoll-send-til-signering-dialog',
    templateUrl: 'møteprotokoll-send-til-signering-dialog.html',
    styleUrl: 'møteprotokoll-send-til-signering-dialog.scss',
    host: {'data-testid': 'møteprotokoll-send-til-signering-dialog'},
    imports: [
        SigneringVelgAksepterteMetoderKomponent,
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatLabel,
        MatProgressBar,
        MatToolbar,
        ReactiveFormsModule,
        TipsKomponent,
        TranslateModule,
        MatCheckbox,
        MatTooltip,
        MedlemmerListevisningKompaktKomponent,
        MeldingSendtIkonKomponent,
        SubmitButtonKomponent,
        RiktekstRedigeringKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteprotokollSendTilSigneringDialogKomponent {

    static vis(dialog: MatDialog, møte: Promise<MøteModell>) {
        return dialog.open<MøteprotokollSendTilSigneringDialogKomponent, DialogParametre, MøteprotokollSendTilSigneringDialogRespons>(MøteprotokollSendTilSigneringDialogKomponent,
          {
              data: {møte} as DialogParametre,
              disableClose: true,
              ariaLabelledBy: 'møteprotokoll-send-til-signering-dialog-tittel',
              minHeight: 500,
              maxWidth: 700
          });
    }

    modus = signal<'skjema' | 'ingensignatarer' | 'ikkepublisert' | 'kvittering'>('skjema');
    moete = signal<MøteModell>(tomMøteModell());
    signatarer = signal<GruppeMedlemListeModell[]>([]);
    signaturmetoderEndret = computed(() => !erLikeListerIgnorerRekkefølge(this.signaturmetoderSattPåGruppe(), this.signaturmetoderValgt()));
    laster = asynkArbeidPågårSignal();
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    skjema = new FormGroup({
        beskjed: new FormControl<HtmlTekst>('', {nonNullable: true, validators: FeltValidatorer.htmlTekst}),
        signaturmetoder: new FormControl<AkseptertSignaturmetode[]>([], {nonNullable: true, validators: Validators.required}),
        huskSignaturmetodeValg: new FormControl(false, {nonNullable: true})
    });

    private signaturmetoderSattPåGruppe = signal<AkseptertSignaturmetode[]>([])
    private signaturmetoderValgt = toSignal(this.skjema.controls.signaturmetoder.valueChanges, {initialValue: []});

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private dialogRef: MatDialogRef<MøteprotokollSendTilSigneringDialogKomponent, MøteprotokollSendTilSigneringDialogRespons>,
                private avhengigheter: MøteprotokollSendTilSigneringDialogKomponentAvhengigheter,
                private navigasjon: Navigasjon,
                private destroyRef: DestroyRef) {
        this.initMøte(parametre.møte);
    }

    async sendTilSignering() {
        await this.lagrer.utfør(async () => {
            await this.avhengigheter.sendTilSignering(this.moete().id, this.somSpesifikasjon());
            this.modus.set('kvittering');
        });
    }

    gaaTilRedigerProtokoll() {
        this.avbryt();
        this.navigasjon.gåTilRedigerMøteProtokoll(this.moete().id);
    }

    async publiserProtokoll() {
        const oppdatertMøte = await this.avhengigheter.publiserProtokoll(this.moete());
        if (erNil(oppdatertMøte)) return;
        this.initMøte(Promise.resolve(oppdatertMøte));
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    ferdig() {
        this.dialogRef.close(true);
    }

    gaaTilHendelseslogg() {
        this.ferdig();
        this.navigasjon.gåTilMøteHendelseslogg(this.moete().id);
    }

    //private hjelpemetoder

    private async initMøte(møtepromise: Promise<MøteModell>) {
        this.modus.set('skjema');
        this.moete.set(await this.laster.utfør(() => møtepromise));

        if (!this.moete().erProtokollPublisert) {
            this.modus.set('ikkepublisert');
            return;
        }

        const laster = this.laster.start();
        this.avhengigheter.signatarer(this.moete().id)
          .pipe(laster.avsluttVedLeveranse(), takeUntilDestroyed(this.destroyRef))
          .subscribe(signatarer => this.setSignatarer(signatarer));

        this.avhengigheter.standardSignaturmetoder(this.moete().eiendeGruppeId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(metoder => this.setStandardSignaturmetoder(metoder));
    }

    private setSignatarer(signatarer: ProtokollførtMøtedeltakerModell[]) {
        this.signatarer.set(signatarer.map(signatar => signatar.medlem));

        if (erTomListe(signatarer)) {
            this.modus.set('ingensignatarer');
            return;
        }
    }

    private setStandardSignaturmetoder(standardSignaturmetoder: AkseptertSignaturmetode[]) {
        this.signaturmetoderSattPåGruppe.set(standardSignaturmetoder);
        this.skjema.controls.signaturmetoder.setValue(standardSignaturmetoder);
    }

    private somSpesifikasjon(): SendProtokollsigneringsInvitasjonSpesifikasjon {
        const {beskjed, signaturmetoder, huskSignaturmetodeValg} = this.skjema.value;
        return {
            beskjed: beskjed!,
            signaturmetoder: signaturmetoder!,
            huskSignaturmetodeValg: huskSignaturmetodeValg!,
        };
    }

}

export type MøteprotokollSendTilSigneringDialogRespons = boolean;

export type DialogParametre = { møte: Promise<MøteModell>; };

@Injectable()
export class MøteprotokollSendTilSigneringDialogKomponentAvhengigheter {

    constructor(private protokollRessurs: ProtokollRessursClient,
                private møteDatatjeneste: MøteDatatjeneste,
                private gruppeDatatjeneste: GruppeDatatjeneste,
                private matDialog: MatDialog) {
    }

    standardSignaturmetoder(id: GruppeId): Observable<AkseptertSignaturmetode[]> {
        return this.gruppeDatatjeneste.hentMedMeta(id).pipe(
          map(gruppe => gruppe.data.oppsett.aksepterteSignaturmetoder));
    }

    signatarer(id: MøteId): Observable<ProtokollførtMøtedeltakerModell[]> {
        return this.protokollRessurs.deltakere(id).somObservable()
          .pipe(map(protokollførte => signatarer(protokollførte)));
    }

    /** Returnerer oppdatert møte dersom protokoll ble publisert, ellers null */
    async publiserProtokoll(møte: MøteModell): Promise<MøteModell | null> {
        const publisert = await firstValueFrom(MøtePubliserDialogKomponent.visPubliserProtokoll(this.matDialog, Promise.resolve(møte)).beforeClosed());
        return publisert ? this.møteDatatjeneste.hentFraFraLokallager(møte.id) : null;
    }

    async sendTilSignering(id: MøteId, spesifikasjon: SendProtokollsigneringsInvitasjonSpesifikasjon) {
        await this.møteDatatjeneste.sendProtokollTilSignering(id, spesifikasjon);
    }
}
